import { faFolderOpen, faPlus } from "@fortawesome/free-solid-svg-icons";
// import ContentLoader from "react-content-loader";
import { TechnologyConstants } from "Constants";
import { CaseControllerSingleton, SavedQueryControllerSingleton, TechnologyControllerSingleton } from "Controllers";
import { useClickOutsideRef } from "Hooks";
import { LocalStorageLogicSingleton } from "Logics/LocalStorageLogic";
import { SavedQueryContext } from "Providers";
import { FC, useCallback, useContext, useEffect, useRef, useState } from "react";
import { TSavedQueryTempDTO } from "Types";
import { QueryTab } from "./QueryTab";
import { QueryTabButton } from "./QueryTabButton";
import { QueryTabOpenMenu } from "./QueryTabOpenMenu";

// styles
import styles from "./queryTabs.module.scss";

type TQueryTabsProps = {
    selectedTechnologyId: number,
    caseId: number,
    addToCaseQueryCount?: (count: number) => void
    addToTechnologyQueryCount?: (technologyId: number, count: number) => void,
    isLoading?: boolean
};

// const QueryTabsLoader = () => (
//     <ContentLoader
//         animate={true}
//         height="15"
//         width="100%"
//         speed={2}
//         backgroundColor="#fcfcfc"
//         foregroundColor="#ecebeb"
//     >
//         <rect x="0" y="0" rx="5" ry="5" width="90%" height="15" /> 
//     </ContentLoader>
// );

export const QueryTabs: FC<TQueryTabsProps> = ({ selectedTechnologyId, caseId, addToCaseQueryCount, addToTechnologyQueryCount, isLoading }: TQueryTabsProps) => {

    // Ref 
    const openQueryMenuRef = useRef<HTMLDivElement>(null);

    // Contexts
    const { savedQuery, updateSavedQuery, isSavedQueriesLoading, updateIsSavedQueriesLoading } = useContext(SavedQueryContext);

    // State
    const [savedQueries, setSavedQueries] = useState<TSavedQueryTempDTO[]>([]);
    const [openQueryIds, setOpenQueryIds] = useState<Set<string>>(new Set<string>());
    const [isLoadTechnologyModalOpen, setIsLoadTechnologyModalOpen] = useState<boolean>(false);

    // Close on click outside menu
    const onClickOutsideOpenMenu = () => {
        setIsLoadTechnologyModalOpen(false);
    };
    useClickOutsideRef(openQueryMenuRef, onClickOutsideOpenMenu);

    // Logic
    const saveQueryIdsToStorage = useCallback((queryIds: Set<string>) => {
        LocalStorageLogicSingleton.setObjecItem<string[]>(`openQueryIds${caseId}-${selectedTechnologyId}`, Array.from(queryIds));
    }, [caseId, selectedTechnologyId]);

    const loadOpenQueryIdsFromStorage = useCallback(() => {
        return LocalStorageLogicSingleton.getObjectItem<string[]>(`openQueryIds${caseId}-${selectedTechnologyId}`);
    }, [caseId, selectedTechnologyId]);

    const getSelectedTechnologySavedQueries = useCallback(async (): Promise<void> => {
        const selectedTechnologySavedQueries: TSavedQueryTempDTO[] = await TechnologyControllerSingleton
            .getTechnologySavedQueriesAsync(selectedTechnologyId);
        if (selectedTechnologySavedQueries && selectedTechnologySavedQueries.length >= 1) {
            setSavedQueries(selectedTechnologySavedQueries);

            const storageQueryIds = loadOpenQueryIdsFromStorage();
            const savedQueryIds = selectedTechnologySavedQueries.map((query) => { return query.guid; });
            const newQueryIds = new Set<string>();

            if (storageQueryIds === null) {
                for (const currentQuery of selectedTechnologySavedQueries) {
                    newQueryIds.add(currentQuery.guid);
                }
            } else {
                storageQueryIds.forEach((queryId) => {
                    if (savedQueryIds.indexOf(queryId) >= 0) {
                        newQueryIds.add(queryId);
                    }
                });
            }

            let didSetSavedQuery = false;
            if (storageQueryIds && storageQueryIds.length > 0) {
                for (const currentId of storageQueryIds) {
                    const savedQuery = selectedTechnologySavedQueries.filter((savedQuery) => { return savedQuery.guid === currentId; });
                    if(savedQuery.length > 0) {
                        updateSavedQuery(savedQuery[0]);
                        didSetSavedQuery = true;
                        break;
                    }
                }
            } 

            if(!didSetSavedQuery) {
                if (newQueryIds.size > 0) {
                    updateSavedQuery(selectedTechnologySavedQueries[0]);
                } else {
                    updateSavedQuery(undefined);
                }
            }

            setOpenQueryIds(newQueryIds);
            saveQueryIdsToStorage(newQueryIds);
        } else {
            setSavedQueries([]);
            updateSavedQuery(undefined);
        }
        updateIsSavedQueriesLoading(false);
    }, [updateSavedQuery, selectedTechnologyId, loadOpenQueryIdsFromStorage, saveQueryIdsToStorage, updateIsSavedQueriesLoading]);

    const memoizedGetCaseSavedQueries = useCallback(async (): Promise<void> => {
        const caseSavedQueries: TSavedQueryTempDTO[] = await CaseControllerSingleton
            .getCaseSavedQueriesAsync(caseId);
        if (caseSavedQueries && caseSavedQueries.length >= 1) {
            setSavedQueries(caseSavedQueries);

            const storageQueryIds = loadOpenQueryIdsFromStorage();
            const savedQueryIds = caseSavedQueries.map((query) => { return query.guid; });
            const newQueryIds = new Set<string>();

            if (storageQueryIds === null) {
                for (const currentQuery of caseSavedQueries) {
                    newQueryIds.add(currentQuery.guid);
                }
            } else {
                storageQueryIds.forEach((queryId) => {
                    if (savedQueryIds.indexOf(queryId) >= 0) {
                        newQueryIds.add(queryId);
                    }
                });
            }

            let didSetSavedQuery = false;
            if (storageQueryIds && storageQueryIds.length > 0) {
                for (const currentId of storageQueryIds) {
                    const savedQuery = caseSavedQueries.filter((savedQuery) => { return savedQuery.guid === currentId; });
                    if(savedQuery.length > 0) {
                        updateSavedQuery(savedQuery[0]);
                        didSetSavedQuery = true;
                        break;
                    }
                }
            } 
            
            if (!didSetSavedQuery && newQueryIds.size > 0) {
                updateSavedQuery(caseSavedQueries[0]);
            }

            setOpenQueryIds(newQueryIds);
            saveQueryIdsToStorage(newQueryIds);
        } else {
            setSavedQueries([]);
            updateSavedQuery(undefined);
            setOpenQueryIds(new Set<string>());
        }
        updateIsSavedQueriesLoading(false);
    }, [caseId, updateSavedQuery, loadOpenQueryIdsFromStorage, saveQueryIdsToStorage, updateIsSavedQueriesLoading]);

    useEffect(() => {
        if (!savedQuery || !savedQueries || (savedQueries && savedQueries.length < 1)) return;

        let isNewSavedQueryInCurrentList = false;

        for (const savedQ of savedQueries) {
            if (savedQ.guid === savedQuery.guid) {
                isNewSavedQueryInCurrentList = true;
            }
        }

        if (!isNewSavedQueryInCurrentList) {
            const newOpenQueryIds = openQueryIds.add(savedQuery.guid);
            saveQueryIdsToStorage(newOpenQueryIds);
            setOpenQueryIds(newOpenQueryIds);
            setSavedQueries([...savedQueries, savedQuery]);
            if (selectedTechnologyId === TechnologyConstants.GENERAL_TECHNOLOGY_ID) {
                if (addToCaseQueryCount) { 
                    addToCaseQueryCount(1);
                }
            } else {
                if (addToTechnologyQueryCount) addToTechnologyQueryCount(selectedTechnologyId, 1);
            }
        }

    }, [savedQueries, savedQuery, openQueryIds, selectedTechnologyId, saveQueryIdsToStorage, addToCaseQueryCount, addToTechnologyQueryCount]);

    useEffect(() => {
        updateIsSavedQueriesLoading(true);
        if (selectedTechnologyId !== TechnologyConstants.GENERAL_TECHNOLOGY_ID) {
            getSelectedTechnologySavedQueries();
        } else if (selectedTechnologyId === TechnologyConstants.GENERAL_TECHNOLOGY_ID) {
            memoizedGetCaseSavedQueries();
        }
    }, [getSelectedTechnologySavedQueries, memoizedGetCaseSavedQueries, selectedTechnologyId, updateIsSavedQueriesLoading]);

    const closeQuery = (currentQuery: TSavedQueryTempDTO) => {
        openQueryIds.delete(currentQuery.guid);

        if(savedQuery?.guid === currentQuery.guid) { 
            for (const currentSavedQuery of savedQueries) {
                if (openQueryIds.has(currentSavedQuery.guid)) {
                    updateSavedQuery(currentSavedQuery);
                    break;
                }
            }
        }

        const newQueryIds = new Set<string>();
        openQueryIds.forEach((queryId) => newQueryIds.add(queryId));

        setOpenQueryIds(newQueryIds);
        saveQueryIdsToStorage(newQueryIds);
        if (openQueryIds.size === 0) {
            updateSavedQuery(undefined);
        }

        setIsLoadTechnologyModalOpen(false);
    };

    const loadQuery = (queryId: string) => {
        setIsLoadTechnologyModalOpen(false);

        const newSet = new Set<string>();
        newSet.add(queryId);
        openQueryIds.forEach((oldQueryId => {
            newSet.add(oldQueryId);
        }));
        setOpenQueryIds(newSet);
        saveQueryIdsToStorage(newSet);

        for (const savedQuery of savedQueries) {
            if (savedQuery.guid === queryId) {
                updateSavedQuery(savedQuery);
                break;
            }
        }
    };

    const onRenameQuery = (queryId: string, name: string) => {
        setSavedQueries(savedQueries.map((query) => {
            if (query.guid === queryId) {
                query.name = name;
                return query;
            } else {
                return query;
            }
        }));
    };

    const onDeleteQuery = (queryId: string) => {
        const newSavedQueriesList = savedQueries.filter((query) => {
            return query.guid !== queryId;
        });
        if(newSavedQueriesList.length === 0) {
            setOpenQueryIds(new Set<string>());
        }
        setSavedQueries(newSavedQueriesList);
    };

    const createNewQueryClick = useCallback(async () => {
        let savedQuery: TSavedQueryTempDTO | null;
        if (selectedTechnologyId === TechnologyConstants.GENERAL_TECHNOLOGY_ID) {
            savedQuery = await SavedQueryControllerSingleton.create(caseId, undefined);
        } else {
            savedQuery = await SavedQueryControllerSingleton.create(undefined, selectedTechnologyId);
        }

        if (!savedQuery) return;

        const newSavedQueries: TSavedQueryTempDTO[] = [];
        for (const savedQuery of savedQueries) {
            newSavedQueries.push(savedQuery);
        }

        
        if(savedQueries.length === 0) {
            newSavedQueries.push(savedQuery);
            const newOpenQueryIds = openQueryIds.add(savedQuery.guid);
            setOpenQueryIds(newOpenQueryIds);
            saveQueryIdsToStorage(newOpenQueryIds);

            updateSavedQuery(savedQuery);
            setSavedQueries(newSavedQueries);
        } else {
            setSavedQueries(newSavedQueries);
            updateSavedQuery(savedQuery);
        }

        if (selectedTechnologyId === TechnologyConstants.GENERAL_TECHNOLOGY_ID) {
            if (savedQueries.length === 0 && addToCaseQueryCount) {
                addToCaseQueryCount(1);
            }
        } else {
            if (savedQueries.length === 0 && addToTechnologyQueryCount) {
                addToTechnologyQueryCount(selectedTechnologyId, 1);
            }
        }

    }, [selectedTechnologyId, savedQueries, updateSavedQuery, caseId, openQueryIds, saveQueryIdsToStorage,
            addToTechnologyQueryCount, addToCaseQueryCount]);
    if (isSavedQueriesLoading || isLoading) {
        return (
            <div className={[styles.queryTabsContainer, styles.isLoading].join(" ")}>
                {/*<QueryTabsLoader />*/}
            </div>
        );
    }
    return (
        <div className={styles.queryTabsContainer}>
            <div className={[styles.queryTabs, styles.scrollbarCustomThinX].join(" ")}>
                {savedQueries.map(currentQuery => {
                    if (!openQueryIds.has(currentQuery.guid)) return null;

                    return (
                        <QueryTab key={currentQuery.guid} query={currentQuery} selectedQueryGuid={savedQuery?.guid} caseId={caseId}
                            selectedTechnologyId={selectedTechnologyId} clickOnQuery={(currentQuery: TSavedQueryTempDTO) => loadQuery(currentQuery.guid)} clickCloseQuery={closeQuery}
                            addToCaseQueryCount={addToCaseQueryCount} addToTechnologyQueryCount={addToTechnologyQueryCount}
                            onRenameQuery={onRenameQuery} onDeleteQuery={onDeleteQuery} />
                    );
                })}
            </div>
            {savedQueries.length !== 0 ?
                <div style={{zIndex: 1}}>
                    <QueryTabButton text="Open" title="Open saved query" icon={faFolderOpen} onClick={() => { setIsLoadTechnologyModalOpen(!isLoadTechnologyModalOpen); }} />
                    {isLoadTechnologyModalOpen ?
                        <div ref={openQueryMenuRef} className={styles.openTabDropdownContainer}>
                            <QueryTabOpenMenu savedQueries={savedQueries} openQueryIds={openQueryIds} loadQuery={loadQuery} />
                        </div>
                        : null}
                </div> 
                : null}
            <div style={{zIndex: 1}}>
                <QueryTabButton text="New" title="Create new query" icon={faPlus} onClick={createNewQueryClick} />
            </div>
        </div>
    );
};